import { createColumnHelper } from "@tanstack/react-table";
import { capitalCase } from "change-case";
import { useMemo } from "react";
import { parsePhoneNumber } from "react-phone-number-input/input";

import { useStorage } from "@smart/itops-hooks-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import {
  TableProps,
  TimePeriod,
  TimeRange,
  Icon,
  Tooltip,
  CellWrapper,
} from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { formatDateTime } from "@smart/itops-utils-basic";

import {
  filterByDatePeriod,
  filterBySelection,
  getOnRowClick,
  getStaffName,
  NO_VALUE,
  sortByStaffName,
  sortSchema,
} from "./column-helpers";
import { useTimeFilterValue } from "./time-filter-value";
import { GqlSubmission, GqlSubmissionSummary } from "../../types";

export const useWebEnquiriesColumns = ({
  onNoMatterRowClick,
  onFlaggedRowClick,
}: {
  onNoMatterRowClick: (row: GqlSubmissionSummary) => void;
  onFlaggedRowClick: (row: GqlSubmissionSummary) => void;
}) => {
  const { terms, country, dateDisplayFormat } = loadLocale();
  const [columnSort, setColumnSort] = useStorage({
    defaultValue: [{ id: "receivedAt", desc: true }],
    key: "web-enquiries-list-sort",
    schema: sortSchema,
    storage: "local",
  });
  const { staff, host } = useSmokeballApp();
  const { timeFilter, setTimeFilter } = useTimeFilterValue({
    filterStorageKey: "receivedAt-date-filter",
  });

  const getDisplayPhoneNumber = (row: GqlSubmissionSummary) => {
    let phone =
      row.values.recipient?.cell || row.values.recipient?.phone || NO_VALUE;
    if (phone !== NO_VALUE) {
      const parsed = parsePhoneNumber(phone, country);
      phone = parsed?.formatNational() || phone;
    }
    return phone;
  };

  const columns = useMemo(() => {
    const col = createColumnHelper<GqlSubmissionSummary>();
    const disabledClassNames = "italic text-neutral-400";
    return [
      col.accessor("form.values.title", {
        header: "Form Name",
        meta: {
          showRowCount: true,
          filter: {
            type: "textInput",
            name: "formName",
            title: "Form Name",
            placeholder: "All",
            order: 1,
          },
        },
        cell: ({ row }) => (
          <CellWrapper
            label={row.original.form.values.title}
            size="medium"
            className={
              !row.original.matter?.id ? disabledClassNames : undefined
            }
          />
        ),
      }),
      col.accessor("values.recipient.contactName", {
        header: "Prospect Name",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.matter?.id ? (
            <CellWrapper
              label={row.original.values.recipient?.contactName || NO_VALUE}
              hideTooltip={!row.original.values.recipient?.contactName}
              size="medium"
            />
          ) : (
            <div className="flex gap-small items-center">
              <span className={disabledClassNames}>Unknown</span>
              <div className="flex rounded-small h-8 w-8 items-center justify-center bg-yellow-450">
                <Tooltip
                  text={
                    row.original.values.verificationStatus === "flagged"
                      ? `This ${terms.enquiry.name} has been flagged for review. Click to approve or delete it.`
                      : "No contact name was captured in this form's submission. Please add a contact to complete lead creation."
                  }
                  offsetBy={15}
                  placement="bottom"
                >
                  <Icon
                    className="w-[1.2rem]"
                    name="solidTriangleExclamation"
                    dataTestId="no-contact-name"
                  />
                </Tooltip>
              </div>
            </div>
          ),
      }),
      col.accessor("values.recipient.email", {
        header: "Email",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <CellWrapper
            label={row.original.values.recipient?.email || NO_VALUE}
            className={
              !row.original.matter?.id ? disabledClassNames : undefined
            }
            hideTooltip={!row.original.values.recipient?.email}
          />
        ),
      }),
      col.accessor(
        (row) =>
          row.values.recipient?.cell || row.values.recipient?.phone || NO_VALUE,
        {
          id: "prospect-phone",
          header: "Phone",
          enableColumnFilter: false,
          cell: ({ row }) => {
            const phone = getDisplayPhoneNumber(row.original);
            return (
              <CellWrapper
                label={phone}
                className={
                  !row.original.matter?.id ? disabledClassNames : undefined
                }
                hideTooltip={phone === NO_VALUE}
              />
            );
          },
          sortingFn: (rowA, rowB) =>
            getDisplayPhoneNumber(rowA.original).localeCompare(
              getDisplayPhoneNumber(rowB.original),
            ),
        },
      ),
      col.accessor("matter.personResponsibleStaffId", {
        id: "personResponsibleStaffId",
        header: terms.responsiblePerson.header,
        meta: {
          filter: {
            type: "select",
            name: "personResponsible",
            title: capitalCase(terms.responsiblePerson.singular),
            order: 2,
            placeholder: "All",
            fixedOptions: [
              { value: "", label: "Unassigned" },
              ...(
                staff?.current?.map((s) => ({
                  value: s.id,
                  label: getStaffName({ staff: staff?.current, staffId: s.id }),
                })) || []
              ).sort((a, b) => a.label.localeCompare(b.label)),
            ],
          },
        },
        cell: ({ row, getValue }) => (
          <CellWrapper
            label={getStaffName({ staff: staff?.current, staffId: getValue() })}
            className={
              !row.original.matter?.id ? disabledClassNames : undefined
            }
          />
        ),
        filterFn: filterBySelection,
        sortingFn: (rowA, rowB) =>
          sortByStaffName({
            staff: staff?.current,
            staffAId: rowA.original.matter?.personResponsibleStaffId,
            staffBId: rowB.original.matter?.personResponsibleStaffId,
          }),
      }),
      col.accessor("values.createdAt", {
        id: "receivedAt",
        header: "Received",
        meta: {
          filter: {
            type: "dateRange",
            name: "createdAt",
            title: "Received",
            order: 3,
            dateRangeExcludedOptions: ["allTime"],
            onFilterValueChanged: (value) => {
              const valueAsTimeFilter = value as unknown as {
                period: TimePeriod;
                range?: TimeRange;
              };
              if (valueAsTimeFilter) {
                setTimeFilter(
                  valueAsTimeFilter.period,
                  valueAsTimeFilter.range,
                );
              }
            },
          },
        },
        sortDescFirst: true,
        cell: ({ row, getValue }) => (
          <CellWrapper
            label={formatDateTime(getValue(), dateDisplayFormat)}
            className={
              !row.original.matter?.id ? disabledClassNames : undefined
            }
          />
        ),
        filterFn: filterByDatePeriod,
      }),
    ];
  }, [staff?.current?.length]);

  const select: TableProps<GqlSubmission>["select"] = {
    state: {},
    onChange: (selected) => {
      const value = typeof selected === "function" ? selected({}) : selected;
      console.debug("selected rows: ", value);
    },
  };

  const sort: TableProps<GqlSubmission>["sort"] = {
    state: columnSort,
    onChange: setColumnSort,
  };

  const onRowClick = (row: GqlSubmissionSummary) => {
    if (row.values.verificationStatus === "flagged") {
      onFlaggedRowClick(row);
      return;
    }
    if (!row.matter?.id) {
      onNoMatterRowClick(row);
      return;
    }
    getOnRowClick(host?.navigate)(row);
  };

  return {
    columns,
    select,
    sort,
    timeFilter,
    setTimeFilter,
    onRowClick,
  };
};
